import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, Search, MapPin, Users, Plus, Edit, Trash2,
  Loader2, Eye, Clock, DollarSign, Mail, Phone, 
  ChevronDown, Building2, FileText, CheckCircle, X,
  EyeOff, UserCheck, UserX, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import ProducerLayout from '../../components/producer/ProducerLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const JOB_TYPES = [
  'Tiempo completo',
  'Part-time',
  'Por evento',
  'Freelance',
  'Temporario'
];

const CandidateCard = ({ candidate, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.01 }}
    onClick={onClick}
    className="bg-[#121212] border border-[#27272A] rounded-xl p-5 cursor-pointer hover:border-violet-500/30 transition-all"
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
        {candidate.user_photo ? (
          <img src={candidate.user_photo} alt="" className="w-10 h-10 rounded-lg object-cover" />
        ) : (
          <Users size={24} className="text-violet-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold mb-1 line-clamp-1">{candidate.user_name || 'Candidato'}</h3>
        <p className="text-sm text-violet-500 mb-2 line-clamp-1">{candidate.headline}</p>
        <div className="flex flex-wrap gap-2 text-xs text-zinc-400">
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {candidate.city}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={12} /> {candidate.experience_years} años exp.
          </span>
          <span className="flex items-center gap-1">
            <Briefcase size={12} /> {candidate.availability}
          </span>
        </div>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-1.5">
      {(candidate.skills || []).slice(0, 4).map(skill => (
        <span key={skill} className="px-2 py-0.5 bg-[#27272A] rounded text-xs text-zinc-400">
          {skill}
        </span>
      ))}
      {(candidate.skills || []).length > 4 && (
        <span className="px-2 py-0.5 text-zinc-500 text-xs">+{candidate.skills.length - 4}</span>
      )}
    </div>
  </motion.div>
);

const ProducerJobsPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('postings'); // 'postings' | 'candidates'
  
  // Postings state
  const [postings, setPostings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [postingModalOpen, setPostingModalOpen] = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postingToDelete, setPostingToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [postingForm, setPostingForm] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    job_type: '',
    salary_range: '',
    requirements: [],
    benefits: [],
    contact_email: '',
    contact_phone: '',
    is_anonymous: false
  });
  const [newRequirement, setNewRequirement] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  
  // Applications state
  const [applicationsModalOpen, setApplicationsModalOpen] = useState(false);
  const [selectedPostingForApps, setSelectedPostingForApps] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  // Candidates state
  const [candidates, setCandidates] = useState([]);
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [candidateFilters, setCandidateFilters] = useState({ skill: '', city: '', search: '' });
  const [availableFilters, setAvailableFilters] = useState({ skills: [], cities: [] });
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    fetchPostings();
    fetchCategories();
    fetchCandidates();
  }, []);

  const fetchPostings = async () => {
    try {
      const response = await api.get('/jobs/postings/my');
      setPostings(response.data.postings);
    } catch (error) {
      console.error('Error fetching postings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/jobs/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const params = new URLSearchParams();
      if (candidateFilters.skill) params.append('skill', candidateFilters.skill);
      if (candidateFilters.city) params.append('city', candidateFilters.city);
      if (candidateFilters.search) params.append('search', candidateFilters.search);
      
      const response = await api.get(`/jobs/candidates?${params.toString()}`);
      setCandidates(response.data.candidates);
      setCandidatesTotal(response.data.total);
      setAvailableFilters(response.data.filters);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchCandidateProfile = async (profileId) => {
    try {
      const response = await api.get(`/jobs/candidates/${profileId}`);
      setSelectedCandidate(response.data);
    } catch (error) {
      toast.error('Error al cargar perfil');
    }
  };

  const handleOpenCreate = () => {
    setEditingPosting(null);
    setPostingForm({
      title: '',
      description: '',
      category: '',
      location: '',
      job_type: '',
      salary_range: '',
      requirements: [],
      benefits: [],
      contact_email: '',
      contact_phone: '',
      is_anonymous: false
    });
    setPostingModalOpen(true);
  };

  const handleOpenEdit = (posting) => {
    setEditingPosting(posting);
    setPostingForm({
      title: posting.title,
      description: posting.description,
      category: posting.category,
      location: posting.location,
      job_type: posting.job_type,
      salary_range: posting.salary_range || '',
      requirements: posting.requirements || [],
      benefits: posting.benefits || [],
      contact_email: posting.contact_email || '',
      contact_phone: posting.contact_phone || '',
      is_anonymous: posting.is_anonymous || false
    });
    setPostingModalOpen(true);
  };
  
  const fetchApplications = async (postingId) => {
    setLoadingApplications(true);
    try {
      const response = await api.get(`/jobs/postings/${postingId}/applications`);
      setApplications(response.data.applications);
    } catch (error) {
      toast.error('Error al cargar aplicantes');
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleViewApplications = (posting) => {
    setSelectedPostingForApps(posting);
    fetchApplications(posting.id);
    setApplicationsModalOpen(true);
  };

  const updateApplicationStatus = async (appId, status) => {
    try {
      await api.put(`/jobs/applications/${appId}/status?status=${status}`);
      toast.success('Estado actualizado');
      fetchApplications(selectedPostingForApps.id);
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleSubmitPosting = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingPosting) {
        await api.put(`/jobs/postings/${editingPosting.id}`, postingForm);
        toast.success('Empleo actualizado');
      } else {
        await api.post('/jobs/postings', postingForm);
        toast.success('Empleo publicado');
      }
      setPostingModalOpen(false);
      fetchPostings();
    } catch (error) {
      const errorData = error.response?.data?.detail;
      let errorMsg = 'Error al guardar';
      if (typeof errorData === 'string') {
        errorMsg = errorData;
      } else if (Array.isArray(errorData)) {
        // Pydantic validation errors come as array of objects
        errorMsg = errorData.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorData?.msg) {
        errorMsg = errorData.msg;
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePosting = async () => {
    if (!postingToDelete) return;
    try {
      await api.delete(`/jobs/postings/${postingToDelete.id}`);
      toast.success('Empleo eliminado');
      setDeleteDialogOpen(false);
      setPostingToDelete(null);
      fetchPostings();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const togglePostingActive = async (posting) => {
    try {
      await api.put(`/jobs/postings/${posting.id}`, { is_active: !posting.is_active });
      toast.success(posting.is_active ? 'Empleo pausado' : 'Empleo activado');
      fetchPostings();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const addRequirement = () => {
    if (!newRequirement.trim()) return;
    setPostingForm({ ...postingForm, requirements: [...postingForm.requirements, newRequirement.trim()] });
    setNewRequirement('');
  };

  const addBenefit = () => {
    if (!newBenefit.trim()) return;
    setPostingForm({ ...postingForm, benefits: [...postingForm.benefits, newBenefit.trim()] });
    setNewBenefit('');
  };

  return (
    <SidebarProvider>
      <ProducerLayout>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                <Briefcase size={28} className="text-black" />
              </div>
              <div>
                <h1 className="text-2xl font-black">Portal de Empleo</h1>
                <p className="text-zinc-500 text-sm">Publicá empleos y encontrá talento</p>
              </div>
            </div>
            {activeTab === 'postings' && (
              <Button onClick={handleOpenCreate} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold">
                <Plus size={18} className="mr-2" /> Publicar Empleo
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setActiveTab('postings')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'postings' ? 'bg-violet-500 text-black' : 'bg-[#121212] text-zinc-400 hover:text-white'
              }`}
            >
              <FileText size={16} className="inline mr-2" />
              Mis Publicaciones ({postings.length})
            </button>
            <button
              onClick={() => setActiveTab('candidates')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'candidates' ? 'bg-violet-500 text-black' : 'bg-[#121212] text-zinc-400 hover:text-white'
              }`}
            >
              <Users size={16} className="inline mr-2" />
              Buscar Candidatos ({candidatesTotal})
            </button>
          </div>

          {/* Postings Tab */}
          {activeTab === 'postings' && (
            <div>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={40} className="animate-spin text-violet-500" />
                </div>
              ) : postings.length === 0 ? (
                <div className="text-center py-20 bg-[#121212] border border-[#27272A] rounded-2xl">
                  <Briefcase size={48} className="mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-xl font-bold mb-2">Sin publicaciones</h3>
                  <p className="text-zinc-500 mb-6">Publicá tu primer empleo para encontrar talento</p>
                  <Button onClick={handleOpenCreate} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold">
                    <Plus size={18} className="mr-2" /> Publicar Empleo
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {postings.map(posting => (
                    <motion.div
                      key={posting.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-[#121212] border border-[#27272A] rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-bold text-lg">{posting.title}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              posting.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {posting.is_active ? 'Activo' : 'Pausado'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-zinc-400 mb-3">
                            <span className="flex items-center gap-1">
                              <MapPin size={14} /> {posting.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} /> {posting.job_type}
                            </span>
                            <span className="flex items-center gap-1">
                              <Briefcase size={14} /> {posting.category}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye size={14} /> {posting.views || 0} vistas
                            </span>
                            <span className="flex items-center gap-1 text-violet-500 cursor-pointer hover:underline" 
                                  onClick={() => handleViewApplications(posting)}>
                              <Users size={14} /> {posting.applications_count || 0} aplicantes
                            </span>
                            {posting.is_anonymous && (
                              <span className="flex items-center gap-1 text-purple-400">
                                <EyeOff size={14} /> Anónimo
                              </span>
                            )}
                          </div>
                          <p className="text-zinc-500 text-sm line-clamp-2">{posting.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleViewApplications(posting)}
                                  className="text-violet-500">
                            <Users size={16} className="mr-1" /> Aplicantes
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => togglePostingActive(posting)}>
                            {posting.is_active ? 'Pausar' : 'Activar'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(posting)}>
                            <Edit size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setPostingToDelete(posting); setDeleteDialogOpen(true); }}
                                  className="text-red-400 hover:text-red-300">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Candidates Tab */}
          {activeTab === 'candidates' && (
            <div>
              {/* Filters */}
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 mb-6">
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      value={candidateFilters.search}
                      onChange={(e) => setCandidateFilters({...candidateFilters, search: e.target.value})}
                      placeholder="Buscar por nombre o perfil..."
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                  </div>
                  <Select value={candidateFilters.skill || "all"} onValueChange={(v) => setCandidateFilters({...candidateFilters, skill: v === "all" ? "" : v})}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                      <SelectValue placeholder="Habilidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {availableFilters.skills?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchCandidates} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white">
                    <Search size={16} className="mr-2" /> Buscar
                  </Button>
                </div>
              </div>

              {/* Candidates Grid */}
              {candidates.length === 0 ? (
                <div className="text-center py-20">
                  <Users size={48} className="mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-xl font-bold mb-2">Sin candidatos</h3>
                  <p className="text-zinc-500">No hay perfiles que coincidan con tu búsqueda</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {candidates.map(candidate => (
                    <CandidateCard key={candidate.id} candidate={candidate} onClick={() => fetchCandidateProfile(candidate.id)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Create/Edit Posting Modal */}
          <Dialog open={postingModalOpen} onOpenChange={setPostingModalOpen}>
            <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPosting ? 'Editar Empleo' : 'Publicar Empleo'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitPosting} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Título del puesto * <span className="text-zinc-500 text-xs font-normal">(mín. 5 caracteres)</span></Label>
                    <Input
                      value={postingForm.title}
                      onChange={(e) => setPostingForm({...postingForm, title: e.target.value})}
                      placeholder="Ej: Bartender para eventos"
                      required
                      minLength={5}
                      className="bg-[#121212] border-[#27272A]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoría *</Label>
                    <Select value={postingForm.category} onValueChange={(v) => setPostingForm({...postingForm, category: v})}>
                      <SelectTrigger className="bg-[#121212] border-[#27272A]">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ubicación *</Label>
                    <Input
                      value={postingForm.location}
                      onChange={(e) => setPostingForm({...postingForm, location: e.target.value})}
                      placeholder="Ej: Buenos Aires, CABA"
                      required
                      className="bg-[#121212] border-[#27272A]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de empleo *</Label>
                    <Select value={postingForm.job_type} onValueChange={(v) => setPostingForm({...postingForm, job_type: v})}>
                      <SelectTrigger className="bg-[#121212] border-[#27272A]">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {JOB_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descripción * <span className="text-zinc-500 text-xs font-normal">(mín. 50 caracteres)</span></Label>
                  <Textarea
                    value={postingForm.description}
                    onChange={(e) => setPostingForm({...postingForm, description: e.target.value})}
                    placeholder="Describí las tareas, responsabilidades y qué buscás..."
                    rows={4}
                    required
                    minLength={50}
                    className="bg-[#121212] border-[#27272A] resize-none"
                  />
                  <p className={`text-xs ${postingForm.description.length >= 50 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {postingForm.description.length}/50 caracteres mínimo
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Rango salarial (opcional)</Label>
                  <Input
                    value={postingForm.salary_range}
                    onChange={(e) => setPostingForm({...postingForm, salary_range: e.target.value})}
                    placeholder="Ej: $50.000 - $80.000 o A convenir"
                    className="bg-[#121212] border-[#27272A]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Requisitos</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      placeholder="Ej: Experiencia mínima 2 años"
                      className="bg-[#121212] border-[#27272A]"
                    />
                    <Button type="button" onClick={addRequirement} variant="outline" className="border-[#27272A]">
                      <Plus size={16} />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {postingForm.requirements.map((req, idx) => (
                      <span key={idx} className="flex items-center gap-2 px-3 py-1 bg-[#27272A] rounded text-sm">
                        {req}
                        <button type="button" onClick={() => setPostingForm({...postingForm, requirements: postingForm.requirements.filter((_, i) => i !== idx)})} 
                                className="text-red-400"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Beneficios</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newBenefit}
                      onChange={(e) => setNewBenefit(e.target.value)}
                      placeholder="Ej: Propinas, comida incluida"
                      className="bg-[#121212] border-[#27272A]"
                    />
                    <Button type="button" onClick={addBenefit} variant="outline" className="border-[#27272A]">
                      <Plus size={16} />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {postingForm.benefits.map((ben, idx) => (
                      <span key={idx} className="flex items-center gap-2 px-3 py-1 bg-[#27272A] rounded text-sm">
                        {ben}
                        <button type="button" onClick={() => setPostingForm({...postingForm, benefits: postingForm.benefits.filter((_, i) => i !== idx)})} 
                                className="text-red-400"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Contact fields - Solo mostrar si NO es anónimo */}
                {!postingForm.is_anonymous && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email de contacto (opcional)</Label>
                      <Input
                        type="email"
                        value={postingForm.contact_email}
                        onChange={(e) => setPostingForm({...postingForm, contact_email: e.target.value})}
                        placeholder="contacto@tuempresa.com"
                        className="bg-[#121212] border-[#27272A]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono de contacto (opcional)</Label>
                      <Input
                        value={postingForm.contact_phone}
                        onChange={(e) => setPostingForm({...postingForm, contact_phone: e.target.value})}
                        placeholder="+54 11 1234-5678"
                        className="bg-[#121212] border-[#27272A]"
                      />
                    </div>
                  </div>
                )}
                
                {/* Anonymous toggle */}
                <div className={`flex items-center justify-between p-4 rounded-xl border ${postingForm.is_anonymous ? 'bg-purple-500/10 border-purple-500/30' : 'bg-[#121212] border-[#27272A]'}`}>
                  <div className="flex items-center gap-3">
                    <EyeOff size={20} className={postingForm.is_anonymous ? 'text-purple-400' : 'text-zinc-400'} />
                    <div>
                      <p className="font-medium">Publicar anónimamente</p>
                      <p className="text-xs text-zinc-500">
                        {postingForm.is_anonymous 
                          ? 'Los candidatos solo podrán aplicar. Recibirás sus perfiles y CVs sin revelar tu identidad.'
                          : 'Tu nombre de productora y datos de contacto serán visibles'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={postingForm.is_anonymous}
                    onCheckedChange={(v) => setPostingForm({...postingForm, is_anonymous: v, contact_email: v ? '' : postingForm.contact_email, contact_phone: v ? '' : postingForm.contact_phone})}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setPostingModalOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold">
                    {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    {editingPosting ? 'Guardar Cambios' : 'Publicar Empleo'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Candidate Detail Modal */}
          <Dialog open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
            <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto">
              {selectedCandidate && (
                <>
                  <DialogHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-violet-500/10 flex items-center justify-center">
                        {selectedCandidate.user_photo ? (
                          <img src={selectedCandidate.user_photo} alt="" className="w-14 h-14 rounded-lg object-cover" />
                        ) : (
                          <Users size={32} className="text-violet-500" />
                        )}
                      </div>
                      <div>
                        <DialogTitle>{selectedCandidate.user_name || 'Candidato'}</DialogTitle>
                        <p className="text-violet-500 font-medium">{selectedCandidate.headline}</p>
                      </div>
                    </div>
                  </DialogHeader>
                  
                  <div className="space-y-5">
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                        <MapPin size={14} className="text-violet-500" /> {selectedCandidate.city}
                      </span>
                      <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                        <Clock size={14} className="text-violet-500" /> {selectedCandidate.experience_years} años exp.
                      </span>
                      <span className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] rounded-lg">
                        <Briefcase size={14} className="text-violet-500" /> {selectedCandidate.availability}
                      </span>
                    </div>

                    <div>
                      <h4 className="font-bold mb-2">Sobre el candidato</h4>
                      <p className="text-zinc-400 whitespace-pre-wrap">{selectedCandidate.bio}</p>
                    </div>

                    <div>
                      <h4 className="font-bold mb-2">Habilidades</h4>
                      <div className="flex flex-wrap gap-2">
                        {(selectedCandidate.skills || []).map(skill => (
                          <span key={skill} className="px-3 py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    {selectedCandidate.certifications?.length > 0 && (
                      <div>
                        <h4 className="font-bold mb-2">Certificaciones</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedCandidate.certifications.map((cert, idx) => (
                            <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-[#121212] rounded text-sm">
                              <CheckCircle size={12} className="text-emerald-400" /> {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCandidate.salary_expectation && (
                      <div className="p-4 bg-[#121212] rounded-lg">
                        <span className="text-zinc-500 text-sm">Pretensión salarial: </span>
                        <span className="font-medium">{selectedCandidate.salary_expectation}</span>
                      </div>
                    )}

                    {selectedCandidate.cv_link && (
                      <a href={selectedCandidate.cv_link} target="_blank" rel="noopener noreferrer"
                         className="flex items-center gap-2 p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl hover:bg-violet-500/20 transition-colors">
                        <FileText size={20} className="text-violet-500" />
                        <span className="font-medium">Ver CV completo</span>
                      </a>
                    )}

                    <div className="bg-[#121212] rounded-xl p-5">
                      <h4 className="font-bold mb-3">Contacto</h4>
                      <div className="space-y-2">
                        {selectedCandidate.user_email && (
                          <a href={`mailto:${selectedCandidate.user_email}`} className="flex items-center gap-2 text-violet-500 hover:underline">
                            <Mail size={16} /> {selectedCandidate.user_email}
                          </a>
                        )}
                        {selectedCandidate.phone && (
                          <a href={`tel:${selectedCandidate.phone}`} className="flex items-center gap-2 text-violet-500 hover:underline">
                            <Phone size={16} /> {selectedCandidate.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Applications Modal */}
          <Dialog open={applicationsModalOpen} onOpenChange={setApplicationsModalOpen}>
            <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users size={20} className="text-violet-500" />
                  Aplicantes a: {selectedPostingForApps?.title}
                </DialogTitle>
              </DialogHeader>
              
              {loadingApplications ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-violet-500" />
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto mb-4 text-zinc-600" />
                  <p className="text-zinc-500">Todavía no hay aplicantes para este empleo</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map(app => (
                    <div key={app.id} className="bg-[#121212] rounded-xl p-4 border border-[#27272A]">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#27272A] flex items-center justify-center overflow-hidden">
                          {app.user_photo ? (
                            <img src={app.user_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Users size={24} className="text-zinc-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-bold">{app.user_name}</p>
                              <p className="text-sm text-violet-500">{app.headline}</p>
                              <p className="text-xs text-zinc-500 mt-1">
                                Aplicó el {new Date(app.created_at).toLocaleDateString('es-AR')}
                              </p>
                            </div>
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                              app.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              app.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                              app.status === 'contacted' ? 'bg-emerald-500/20 text-emerald-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {app.status === 'pending' ? 'Pendiente' :
                               app.status === 'reviewed' ? 'Revisado' :
                               app.status === 'contacted' ? 'Contactado' : 'Rechazado'}
                            </div>
                          </div>
                          
                          {app.message && (
                            <div className="mt-3 p-3 bg-[#0A0A0A] rounded-lg">
                              <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
                                <MessageCircle size={12} /> Mensaje del candidato:
                              </p>
                              <p className="text-sm text-zinc-300">{app.message}</p>
                            </div>
                          )}
                          
                          {app.profile && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {app.profile.skills?.slice(0, 4).map((skill, i) => (
                                <span key={i} className="px-2 py-1 bg-[#27272A] rounded text-xs">{skill}</span>
                              ))}
                              {app.profile.experience_years && (
                                <span className="px-2 py-1 bg-[#27272A] rounded text-xs">
                                  {app.profile.experience_years} años exp.
                                </span>
                              )}
                            </div>
                          )}
                          
                          <div className="mt-3 flex items-center gap-2">
                            {app.profile?.user_email && (
                              <a href={`mailto:${app.profile.user_email}`} 
                                 className="text-xs text-violet-500 hover:underline flex items-center gap-1">
                                <Mail size={12} /> {app.profile.user_email}
                              </a>
                            )}
                            {app.profile?.cv_link && (
                              <a href={app.profile.cv_link} target="_blank" rel="noopener noreferrer"
                                 className="text-xs text-violet-500 hover:underline flex items-center gap-1">
                                <FileText size={12} /> Ver CV
                              </a>
                            )}
                          </div>
                          
                          <div className="mt-3 flex items-center gap-2">
                            <Button size="sm" variant="outline" 
                                    className="border-[#27272A] text-xs h-7"
                                    onClick={() => updateApplicationStatus(app.id, 'reviewed')}>
                              <Eye size={12} className="mr-1" /> Marcar revisado
                            </Button>
                            <Button size="sm" variant="outline" 
                                    className="border-emerald-500/30 text-emerald-400 text-xs h-7"
                                    onClick={() => updateApplicationStatus(app.id, 'contacted')}>
                              <UserCheck size={12} className="mr-1" /> Contactado
                            </Button>
                            <Button size="sm" variant="outline" 
                                    className="border-red-500/30 text-red-400 text-xs h-7"
                                    onClick={() => updateApplicationStatus(app.id, 'rejected')}>
                              <UserX size={12} className="mr-1" /> Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent className="bg-[#121212] border-[#27272A]">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. La publicación "{postingToDelete?.title}" será eliminada permanentemente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-[#27272A]">Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeletePosting} className="bg-red-500 hover:bg-red-600">
                  <Trash2 size={16} className="mr-2" /> Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </ProducerLayout>
    </SidebarProvider>
  );
};

export default ProducerJobsPage;
